/**
 * Tasks: upcoming/overdue/completed lists, month calendar, add/edit with recurrence
 * and reminders, detail with attachments.
 */
import { PRIORITIES, RECURRENCE_FREQUENCIES, TASK_STATUSES } from "@shared/constants.ts";
import { addDays, addMonths, daysInMonth, makeDate, parts, today } from "@shared/dates.ts";
import { describeRule } from "@shared/recurrence.ts";
import { taskSchema } from "@shared/schemas/operations.ts";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  DateField,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { type Column, DataTable, Pagination } from "../../components/ui/DataTable.tsx";
import { FilterBar, FilterSelect, SearchInput, ViewTabs } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  PageHeader,
  QueryState,
  Section,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useSortState, useUrlState } from "../../lib/url-state.ts";

export interface TaskRow {
  id: string;
  title: string;
  description: string;
  propertyId: string | null;
  tenancyId: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  completedDate: string | null;
  status: string;
  priority: string;
  recurrenceFrequency: string | null;
  recurrenceInterval: number | null;
  recurrenceUntilDate: string | null;
  reminderDays: number[];
  notes: string;
  addressLine1: string | null;
}

function isOverdue(t: TaskRow) {
  return !!t.dueDate && !["done", "cancelled"].includes(t.status) && t.dueDate < today();
}

function CompleteButton({ task }: { task: TaskRow }) {
  const invalidate = useInvalidateAll();
  const { can } = useAuth();
  if (!can("tasks:write") || ["done", "cancelled"].includes(task.status)) return null;
  return (
    <Button
      size="sm"
      variant="outline-success"
      title="Mark complete"
      aria-label={`Mark "${task.title}" complete`}
      onClick={async () => {
        await api.post(`/api/tasks/${task.id}/complete`);
        await invalidate();
      }}
    >
      <Icon name="check" size={14} />
    </Button>
  );
}

export function TaskTable({ filters, compact }: { filters: Params; compact?: boolean }) {
  const f = useFormat();
  const { sort, onSort, page, setPage } = useSortState();
  const q = useApi<{ items: TaskRow[]; total: number; page: number; pageSize: number }>(
    "/api/tasks",
    { ...filters, sort: sort.sort, dir: sort.dir, page },
    { keepPrevious: true },
  );
  const columns: Column<TaskRow>[] = [
    { key: "done", header: "", cell: (t) => <CompleteButton task={t} />, className: "text-center" },
    {
      key: "title",
      header: "Task",
      sort: "title",
      cell: (t) => (
        <>
          <span className="fw-semibold">{t.title}</span>
          {t.recurrenceFrequency && <Icon name="history" size={12} className="ms-1 text-muted" />}
          {!compact && t.addressLine1 && <div className="small text-muted">{t.addressLine1}</div>}
        </>
      ),
    },
    {
      key: "due",
      header: "Due",
      sort: "due",
      cell: (t) => <span className={isOverdue(t) ? "text-danger fw-semibold" : ""}>{f.date(t.dueDate)}</span>,
    },
    ...(compact
      ? []
      : [
          {
            key: "priority",
            header: "Priority",
            sort: "priority",
            cell: (t: TaskRow) => <StatusBadge status={t.priority} />,
          },
          { key: "assignee", header: "Assignee", cell: (t: TaskRow) => t.assigneeName ?? "—" },
        ]),
    {
      key: "status",
      header: "Status",
      cell: (t) => (
        <StatusBadge
          status={isOverdue(t) ? "overdue" : t.status}
          label={isOverdue(t) ? "Overdue" : f.label(TASK_STATUSES, t.status)}
        />
      ),
    },
  ];
  return (
    <QueryState query={q}>
      {(data) => (
        <>
          <DataTable
            rows={data.items}
            columns={columns}
            rowKey={(t) => t.id}
            rowLink={(t) => `/tasks/${t.id}`}
            sort={sort}
            onSort={onSort}
            empty={
              compact ? (
                <p className="text-muted small p-3 mb-0">No tasks.</p>
              ) : (
                <EmptyState
                  icon="tasks"
                  title="No tasks here"
                  action={
                    <IconButtonLink
                      to={withQuery("/tasks/new", { propertyId: filters.propertyId as string })}
                      icon="plus"
                    >
                      Add task
                    </IconButtonLink>
                  }
                >
                  Track repairs to arrange, inspections, renewals and follow-ups. Recurring tasks create their
                  next occurrence automatically.
                </EmptyState>
              )
            }
          />
          {!compact && (
            <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
          )}
        </>
      )}
    </QueryState>
  );
}

function TaskCalendar({ filters }: { filters: Params }) {
  const { get, set } = useUrlState();
  const month = get("month") || today().slice(0, 7);
  const first = `${month}-01`;
  const { y, m } = parts(first);
  const last = makeDate(y, m, daysInMonth(y, m));
  const q = useApi<{ items: TaskRow[] }>("/api/tasks", {
    ...filters,
    view: "all",
    from: first,
    to: last,
    pageSize: 500,
  });
  // Grid starts on Monday.
  const startOffset = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7;
  const gridStart = addDays(first, -startOffset);
  const days = Array.from({ length: Math.ceil((startOffset + daysInMonth(y, m)) / 7) * 7 }, (_, i) =>
    addDays(gridStart, i),
  );
  const byDay = new Map<string, TaskRow[]>();
  for (const t of q.data?.items ?? [])
    if (t.dueDate) byDay.set(t.dueDate, [...(byDay.get(t.dueDate) ?? []), t]);
  const label = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${first}T00:00:00Z`),
  );
  const now = today();
  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => set({ month: addMonths(first, -1).slice(0, 7) })}
        >
          ‹ Previous
        </Button>
        <h2 className="h6 mb-0">{label}</h2>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => set({ month: addMonths(first, 1).slice(0, 7) })}
        >
          Next ›
        </Button>
      </div>
      <section className="calendar-grid" aria-label={`Tasks in ${label}`}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="calendar-cell fw-semibold text-center" style={{ minHeight: 0 }}>
            {d}
          </div>
        ))}
        {days.map((d) => (
          <div
            key={d}
            className={`calendar-cell ${d.slice(0, 7) !== month ? "muted" : ""} ${d === now ? "today" : ""}`}
          >
            <div className="text-end">{parts(d).d}</div>
            {(byDay.get(d) ?? []).map((t) => (
              <Link
                key={t.id}
                to={`/tasks/${t.id}`}
                className={`calendar-task ${isOverdue(t) ? "overdue" : ""} ${t.status === "done" ? "text-decoration-line-through text-muted" : ""}`}
                title={t.title}
              >
                {t.title}
              </Link>
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}

export function TaskListPage() {
  const { get } = useUrlState();
  const lookups = useLookups();
  const view = get("view") || "open";
  const layout = get("layout") || "list";
  const filters: Params = { propertyId: get("propertyId"), assignee: get("assignee"), q: get("q") };
  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle="Upcoming, overdue and completed work"
        actions={
          <Can permission="tasks:write">
            <IconButtonLink to="/tasks/new" icon="plus">
              Add task
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <FilterBar
          actions={
            <ViewTabs
              param="layout"
              defaultValue="list"
              options={[
                { value: "list", label: "List" },
                { value: "calendar", label: "Calendar" },
              ]}
            />
          }
        >
          {layout === "list" && (
            <ViewTabs
              param="view"
              defaultValue="open"
              options={[
                { value: "open", label: "All open" },
                { value: "overdue", label: "Overdue" },
                { value: "upcoming", label: "Upcoming" },
                { value: "completed", label: "Completed" },
              ]}
            />
          )}
          <SearchInput placeholder="Search tasks" />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
          <FilterSelect
            param="assignee"
            label="Assigned"
            options={[{ value: "me", label: "Me" }]}
            allLabel="Anyone"
          />
        </FilterBar>
        {layout === "calendar" ? (
          <TaskCalendar filters={filters} />
        ) : (
          <TaskTable filters={{ ...filters, view }} />
        )}
      </Card>
    </>
  );
}

export function TaskFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  return (
    <EntityForm
      schema={taskSchema}
      resource="/api/tasks"
      id={id}
      title={id ? "Edit task" : "Add task"}
      breadcrumbs={[{ label: "Tasks", to: "/tasks" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        title: params.get("title") ?? "",
        description: "",
        propertyId: params.get("propertyId") ?? "",
        tenancyId: params.get("tenancyId") ?? "",
        assigneeId: "",
        dueDate: "",
        status: "open",
        priority: "normal",
        recurrence: { frequency: "monthly", interval: 1, untilDate: "" },
        reminderDays: [3],
        notes: "",
      }}
      toForm={(r) => ({
        ...Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v])),
        recurrence: r.recurrenceFrequency
          ? {
              frequency: r.recurrenceFrequency,
              interval: r.recurrenceInterval ?? 1,
              untilDate: r.recurrenceUntilDate ?? "",
            }
          : { frequency: "", interval: 1, untilDate: "" },
      })}
      redirect={(saved) => `/tasks/${saved.id}`}
      cancelTo={id ? `/tasks/${id}` : "/tasks"}
    >
      {(form) => {
        const propertyId = form.watch("propertyId");
        const reminders = (form.watch("reminderDays") as number[] | undefined) ?? [];
        return (
          <Row>
            <Col md={8}>
              <TextField name="title" label="Title" required placeholder="e.g. Arrange gas safety check" />
            </Col>
            <Col md={4}>
              <DateField name="dueDate" label="Due date" />
            </Col>
            <Col md={4}>
              <SelectField name="priority" label="Priority" options={PRIORITIES} />
            </Col>
            <Col md={4}>
              <SelectField name="status" label="Status" options={TASK_STATUSES} />
            </Col>
            <Col md={4}>
              <SelectField
                name="assigneeId"
                label="Assigned to"
                options={toOptions(lookups?.members)}
                placeholder="Unassigned"
              />
            </Col>
            <Col md={6}>
              <SelectField
                name="propertyId"
                label="Property"
                options={toOptions(lookups?.properties)}
                placeholder="Not property-specific"
              />
            </Col>
            <Col md={6}>
              <SelectField
                name="tenancyId"
                label="Tenancy"
                options={toOptions(lookups?.tenancies, (t) => !propertyId || t.propertyId === propertyId)}
                placeholder="None"
              />
            </Col>
            <Col md={12}>
              <TextAreaField name="description" label="Description" />
            </Col>
            <Col md={12}>
              <Section title="Repeat & reminders">
                <Row>
                  <Col md={4}>
                    <SelectField
                      name="recurrence.frequency"
                      label="Repeats"
                      options={RECURRENCE_FREQUENCIES}
                      placeholder="Does not repeat"
                      hint="The next task is created when this one is completed."
                    />
                  </Col>
                  <Col md={4}>
                    <NumberField name="recurrence.interval" label="Every" min={1} max={60} />
                  </Col>
                  <Col md={4}>
                    <DateField name="recurrence.untilDate" label="Until (optional)" />
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="reminder-days">
                      <Form.Label>Remind me (days before due)</Form.Label>
                      <Form.Control
                        defaultValue={reminders.join(", ")}
                        placeholder="e.g. 7, 1"
                        onChange={(e) =>
                          form.setValue(
                            "reminderDays",
                            e.target.value
                              .split(/[,\s]+/)
                              .filter(Boolean)
                              .map(Number)
                              .filter((n) => Number.isInteger(n) && n >= 0),
                          )
                        }
                      />
                      <Form.Text className="text-muted">
                        Comma-separated. Reminders appear in notifications and emails.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Section>
            </Col>
            <Col md={12}>
              <TextAreaField name="notes" label="Notes" rows={2} />
            </Col>
          </Row>
        );
      }}
    </EntityForm>
  );
}

export function TaskDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<TaskRow>(`/api/tasks/${id}`);
  return (
    <QueryState query={q}>
      {(t) => (
        <>
          <PageHeader
            title={t.title}
            subtitle={
              <>
                <StatusBadge
                  status={isOverdue(t) ? "overdue" : t.status}
                  label={isOverdue(t) ? "Overdue" : f.label(TASK_STATUSES, t.status)}
                />{" "}
                <StatusBadge status={t.priority} label={`${t.priority} priority`} />
              </>
            }
            breadcrumbs={[{ label: "Tasks", to: "/tasks" }, { label: t.title }]}
            actions={
              <Can permission="tasks:write">
                {["done", "cancelled"].includes(t.status) ? (
                  <Button
                    variant="outline-secondary"
                    onClick={async () => {
                      await api.post(`/api/tasks/${t.id}/reopen`);
                      await invalidate();
                    }}
                  >
                    Reopen
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    onClick={async () => {
                      const r = await api.post<{ nextId: string | null }>(`/api/tasks/${t.id}/complete`);
                      await invalidate();
                      if (r.nextId) navigate(`/tasks/${r.nextId}`);
                    }}
                  >
                    <Icon name="check" size={16} /> Mark complete
                  </Button>
                )}
                <IconButtonLink to={`/tasks/${t.id}/edit`} icon="edit" variant="outline-primary">
                  Edit
                </IconButtonLink>
                <ConfirmButton
                  size="md"
                  title="Delete this task?"
                  body="This can't be undone from the app, but is recorded in the audit history."
                  onConfirm={async () => {
                    await api.delete(`/api/tasks/${t.id}`);
                    await invalidate();
                    navigate("/tasks");
                  }}
                >
                  Delete
                </ConfirmButton>
              </Can>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Details">
                <DefinitionList
                  items={[
                    ["Due", f.date(t.dueDate)],
                    ["Completed", f.date(t.completedDate)],
                    ["Assigned to", t.assigneeName],
                    [
                      "Property",
                      t.propertyId ? (
                        <Link key="p" to={`/properties/${t.propertyId}`}>
                          {t.addressLine1}
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Tenancy",
                      t.tenancyId ? (
                        <Link key="t" to={`/tenancies/${t.tenancyId}`}>
                          View tenancy
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Repeats",
                      t.recurrenceFrequency
                        ? describeRule({
                            frequency: t.recurrenceFrequency as never,
                            interval: t.recurrenceInterval ?? 1,
                            until: t.recurrenceUntilDate,
                          })
                        : "No",
                    ],
                    [
                      "Reminders",
                      t.reminderDays.length
                        ? t.reminderDays
                            .map((d) => (d === 0 ? "on the day" : `${d} day${d === 1 ? "" : "s"} before`))
                            .join(", ")
                        : "None",
                    ],
                  ]}
                />
                {t.description && (
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {t.description}
                  </p>
                )}
                {t.notes && (
                  <p className="text-muted mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {t.notes}
                  </p>
                )}
              </Section>
              <AuditHistory entityType="task" entityId={t.id} />
            </Col>
            <Col lg={4}>
              <Attachments entityType="task" entityId={t.id} title="Attachments" />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
